"use client";

import { IconCheck } from "@tabler/icons-react";
import clsx from "clsx";
import React from "react";
import type { ControlSize, ThemeColor } from "../../../lib/FrontendTypes";
import Ripple from "../Ripple/Ripple";
import Typography, { type TypographyVariant } from "../Typography/Typography";
import "./Button.css";

const CSS_VARIABLE = /var\((--[^)]+)\)/;

const styles: Record<string, string> = {
  button: "calmlens-button-button",
  base: "calmlens-button-base",
  "variant-outline": "calmlens-button-variant-outline",
  disabled: "calmlens-button-disabled",
  loading: "calmlens-button-loading",
  "variant-bare": "calmlens-button-variant-bare",
  "color-primary": "calmlens-button-color-primary",
  success: "calmlens-button-success",
  "shape-round": "calmlens-button-shape-round",
  "size-lg": "calmlens-button-size-lg",
  "size-md": "calmlens-button-size-md",
  "size-sm": "calmlens-button-size-sm",
  successOverlay: "calmlens-button-successOverlay",
  "color-secondary": "calmlens-button-color-secondary",
  "color-accent": "calmlens-button-color-accent",
  "color-dark": "calmlens-button-color-dark",
  "color-light": "calmlens-button-color-light",
  "color-foreground": "calmlens-button-color-foreground",
  "color-success": "calmlens-button-color-success",
  "color-warning": "calmlens-button-color-warning",
  "color-danger": "calmlens-button-color-danger",
  "color-info": "calmlens-button-color-info",
};

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color"> {
  color?: ThemeColor;
  variant?: "bare" | "normal" | "outline";

  label: string;
  as?: React.ElementType;
  size?: ControlSize;
  /** `pill` rounds a text button's ends; `circle` makes an icon button round. */
  shape?: "default" | "pill" | "circle";
  backgroundColor?: string;
  href?: unknown;

  ref?: React.Ref<HTMLButtonElement | HTMLAnchorElement>;

  loading?: boolean;
  success?: boolean;

  noDisabledStyle?: boolean;

  ripple?: boolean;
}

const Button = (props: ButtonProps) => {
  const {
    as,
    noDisabledStyle,
    className,
    loading: propLoading,
    backgroundColor,
    success,
    type,
    disabled,
    size,
    shape,
    ref,
    variant,
    style,
    label,
    href,
    color: colorProp,
    children,
    ripple,
    onClick,
    ...otherProps
  } = props;

  const color =
    colorProp ?? (variant === undefined || variant === "normal" ? "primary" : "neutral");

  const backgroundVariable = backgroundColor ? CSS_VARIABLE.exec(backgroundColor)?.[1] : undefined;
  const resolvedDisabled = disabled;
  const actualDisabled = disabled || propLoading || success;
  // A link stays a link while it is loading or disabled: swapping it for a <button> would replace the
  // element (cutting its animations short and dropping focus). It ignores clicks instead.
  const isLink = Boolean(href) || as !== undefined;
  const Tag = (isLink ? (as ?? "a") : "button") as React.ElementType;
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isLink && actualDisabled) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };

  const textVariant: TypographyVariant =
    size === "sm" ? "bodyExtraSmall" : size === "lg" ? "body" : "bodySmall";

  const renderText = (text: string, key?: React.Key) => (
    <Typography key={key} as="span" variant={textVariant} weight="medium">
      {text}
    </Typography>
  );

  return (
    <Tag
      ref={ref as any}
      href={href}
      disabled={isLink ? undefined : actualDisabled}
      aria-disabled={isLink ? actualDisabled || undefined : resolvedDisabled}
      aria-busy={propLoading || undefined}
      aria-label={label}
      className={clsx(
        styles.button,
        color === "neutral" ? styles.base : color ? styles[`color-${color}`] : undefined,
        styles[`variant-${variant || "normal"}`],
        size && styles[`size-${size}`],
        (shape === "pill" || shape === "circle") && styles["shape-round"],
        resolvedDisabled && !noDisabledStyle && styles.disabled,
        propLoading && styles.loading,
        success && styles.success,
        className,
      )}
      type={isLink ? undefined : type || "button"}
      onClick={handleClick}
      style={{
        backgroundColor,
        // A theme variable's paired foreground. Callers passing a literal color set the text color
        // themselves, so no color parser ships with every page that renders a button.
        color: backgroundVariable ? `var(${backgroundVariable}-foreground)` : undefined,
        borderColor: backgroundColor,
        "--color-button": backgroundColor,
        "--color-ring": backgroundColor,
        ...style,
      }}
      {...otherProps}
    >
      {ripple !== false && !resolvedDisabled ? <Ripple /> : null}
      {success ? (
        <div className={styles.successOverlay}>
          <IconCheck />
        </div>
      ) : null}
      {children !== undefined
        ? React.Children.map(children, (child, index) => {
            if (typeof child === "string" && child) {
              return renderText(child, index);
            }
            return child;
          })
        : renderText(label)}
    </Tag>
  );
};

export default React.memo(Button);
